/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* eslint-disable no-await-in-loop */
import { getOpenAction } from "selectors/pagedViewState";
import { shouldAllowInitialZoomLevelChange } from "selectors/zoom";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { logJSActionsBusyLoopData } from "javascript/loggingUtils";
import { getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { getPageWidth } from "selectors/pageSelectors";
import { PDF_ACTIONS, SKIP_PDF_NAMED_ACTIONS } from "constants/enums";
import { performPdfActions, initJSEngine, setNewLocation } from "pdfActionManager";
import {
  DocOpenEvent,
  DocWillPrintEvent,
  DocDidPrintEvent,
  DocWillSaveEvent,
  DocDidSaveEvent,
  DocWillCloseEvent
} from "jsEvents/docEvents";
import {
  getDocOpenActions,
  getDocWillPrintActions,
  getDocDidPrintActions,
  getDocWillSaveActions,
  getDocDidSaveActions,
  getDocWillCloseActions,
  getTotalPages,
  isSinglePageMode,
  isFullScreenMode,
  getIsDocOpenActionsCompleted
} from "selectors/documentSelectors";
import { logJSActionsData } from "logging";
import { PAGES_VIEW_MARGIN_LEFT, PAGES_VIEW_MARGIN_RIGHT } from "constants/constants";
import { pageChangeHandler } from "utils/PageActionsUtils";
import { getCurrentPageNumber } from "selectors/pageNumber";
import { isZoomFeatureEnabled } from "selectors/appSelectors";
import { recordExecuteDocOpenActionsStart, recordExecuteDocOpenActionsEnd } from "utils/performance";

const getShouldSetFitWidth = stores => {
  if (isSinglePageMode(stores)) return false;
  if (isFullScreenMode(stores)) return false;
  if (isCurrentDeviceMobile()) return true;

  const totalNumberOfPages = getTotalPages(stores);
  let shouldSetFitWidth = false;
  for (let pageIndex = 0; pageIndex < totalNumberOfPages; ++pageIndex) {
    if (
      getPageWidth(stores, pageIndex) >
      getViewPortWidthWithoutPagesViewScrollbar(stores) - (PAGES_VIEW_MARGIN_RIGHT + PAGES_VIEW_MARGIN_LEFT)
    ) {
      shouldSetFitWidth = true;
      break;
    }
  }

  return shouldSetFitWidth;
};

const getWidthUpdateActionList = stores => {
  const viewerActions = [];
  if (shouldAllowInitialZoomLevelChange(stores) && getShouldSetFitWidth(stores)) {
    const fitWidthAction = {
      actionType: PDF_ACTIONS.NAMED,
      actionData: { name: PDF_ACTIONS.NAMED_ACTIONS.FIT_WIDTH }
    };
    viewerActions.push(fitWidthAction);
  }
  return viewerActions;
};

const executeOpenActionList = (stores, actionsList) => {
  for (const action of actionsList) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventData = {};
      if (action.actionData.hasOwnProperty("name")) {
        const targetName = action.actionData.name;
        eventData.targetName = targetName;
        eventData.fileName = `Document-Level:${targetName}`;
      }

      const eventObj = new DocOpenEvent(eventData);
      performPdfActions(stores, [action], { eventObj });
    } else {
      performPdfActions(stores, [action], {});
    }
  }
};

const performAppOpenAction = stores => {
  // (x, y) are on view coordinates at zoom level 1
  const { pageIndex, x = 0, y = 0, zoomLevel } = getOpenAction(stores);

  if (zoomLevel && isZoomFeatureEnabled(stores)) {
    stores.zoom.performZoomAction({ newZoomLevel: zoomLevel });
  }

  if (pageIndex >= 0) {
    setNewLocation(stores, pageIndex, x, y);
  }
};

// This function tries the fit width action, but will not work if we have open state actions via app state props
export const performAppSetWidthOpenAction = stores => {
  executeOpenActionList(stores, getWidthUpdateActionList(stores));
};

const getFilteredDocOpenActions = docOpenActions =>
  docOpenActions.filter(action => {
    const { actionType, actionData } = action;
    switch (actionType) {
      case PDF_ACTIONS.NAMED:
        return !SKIP_PDF_NAMED_ACTIONS.includes(actionData.name);
      case PDF_ACTIONS.GOTO:
        return false;
      case PDF_ACTIONS.RESET_FORM:
        return false;
      case PDF_ACTIONS.HIDE:
        return false;
      default:
        return true;
    }
  });

const checkForValidOpenActions = actions => {
  let isValid = false;
  Object.keys(actions).forEach(key => {
    if (actions[key] !== undefined) {
      isValid = true;
    }
  });
  return isValid;
};
/**
 * This method executes actions on opening pdf according to various conditions mentioned below
 *
 * App state actions are given higher priority than PDF open actions. Hence they we execute them and then the pdf actions with some ignored actions.
 *
 * If App Level Actions present
 *    - via props in 'enableFeatureController', then set App Open Actions and they will be executed
 *    - If dcapi also have some properties to set, they will be ignored if App Open Actions are present
 *    - If App Open Actions not present via props, then dcapi properties will be set if they have actions, if set they will be executed
 *
 * If any of App level actions are present via App open Action or Dcapi, then we will remove Navigation and Zoom actions from the doc open PDF actions and execute those
 * else, if no app level actions are present, execute all the doc open PDF Actions.
 * If no App level or pdf level actions are present, we execute default zoom logic
 */
export const executeDocOpenActions = stores => {
  try {
    if (!getIsDocOpenActionsCompleted(stores)) {
      recordExecuteDocOpenActionsStart();
      const startTime = performance.now();
      performAppOpenAction(stores);
      let docOpenActions = getDocOpenActions(stores);
      const viewerOpenAction = getOpenAction(stores);
      if (checkForValidOpenActions(viewerOpenAction)) {
        docOpenActions = getFilteredDocOpenActions(docOpenActions);
      }
      if (docOpenActions && docOpenActions.length !== 0) {
        stores.documentStore.setIsFilteredDocOpenActionsPresent(true);
      }
      executeOpenActionList(stores, docOpenActions);
      logJSActionsData({ docOpenActionsExecutionTime: performance.now() - startTime });
      logJSActionsBusyLoopData("docOpen");
      stores.documentStore.setDocOpenActionsCompleted(true);
      recordExecuteDocOpenActionsEnd();
    }
  } finally {
    // Special case for when both doc actions and 1st page has actions, initially doc actions should be executed and then page actions.
    stores.documentStore.setDocOpenActionsCompleted(true);
    pageChangeHandler(stores, getCurrentPageNumber(stores));
  }
};

// Need to only perform JS actions for WillPrint/DidPrint//WillSave/DidSave/WillClose
// Ref PDF Spec 12.6.3 Trigger Events

export const executeWillPrintActions = async stores => {
  const actions = getDocWillPrintActions(stores);
  if (!actions.length) return;

  const jsEngine = await initJSEngine(stores);
  if (!jsEngine) return;

  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventObj = new DocWillPrintEvent();
      await performPdfActions(stores, [action], { eventObj });
    }
  }
};

export const executeDidPrintActions = async stores => {
  const actions = getDocDidPrintActions(stores);
  if (!actions.length) return;

  const jsEngine = await initJSEngine(stores);
  if (!jsEngine) return;

  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventObj = new DocDidPrintEvent();
      await performPdfActions(stores, [action], { eventObj });
    }
  }
};

export const executeWillSaveActions = async stores => {
  const actions = getDocWillSaveActions(stores);
  if (!actions.length) return;

  const jsEngine = await initJSEngine(stores);
  if (!jsEngine) return;

  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventObj = new DocWillSaveEvent();
      await performPdfActions(stores, [action], { eventObj });
    }
  }
};

export const executeDidSaveActions = async stores => {
  const actions = getDocDidSaveActions(stores);
  if (!actions.length) return;

  const jsEngine = await initJSEngine(stores);
  if (!jsEngine) return;

  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventObj = new DocDidSaveEvent();
      await performPdfActions(stores, [action], { eventObj });
    }
  }
};

export const executeDocWillCloseActions = async stores => {
  const actions = getDocWillCloseActions(stores);
  if (!actions.length) return;

  const jsEngine = await initJSEngine(stores);
  if (!jsEngine) return;

  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.JAVASCRIPT) {
      const eventObj = new DocWillCloseEvent();
      await performPdfActions(stores, [action], { eventObj });
    }
  }
};

export const executeFormsNonJSDocActions = stores => {
  const actions = getDocOpenActions(stores);
  for (const action of actions) {
    if (action.actionType === PDF_ACTIONS.RESET_FORM || action.actionType === PDF_ACTIONS.HIDE) {
      performPdfActions(stores, [action], {});
    }
  }
};
